import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Paper,
	List,
	ListItem,
	ListItemText,
	Divider,
	Chip,
} from "@mui/material";

const TermsAndConditions = () => {
	return (
		<Box sx={{ bgcolor: "#F9FAFB", minHeight: "100vh", py: { xs: 6, md: 8 } }}>
			<Container maxWidth="lg">
				<Paper
					elevation={0}
					sx={{
						p: { xs: 3, md: 5 },
						borderRadius: 3,
						mb: 4,
						background:
							"linear-gradient(135deg, rgba(62,123,250,0.08), rgba(10,31,68,0.04))",
					}}
				>
					<Chip label="Terms & Conditions" color="primary" sx={{ mb: 2 }} />
					<Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
						The Rules That Keep Orucore Safe
					</Typography>
					<Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
						Effective Date: 9 February 2026
					</Typography>
					<Typography variant="body1" sx={{ maxWidth: 900 }}>
						These Terms & Conditions ("Terms") govern your access to and use of
						Orucore - CRM tool ("Orucore", "we", "our", or "us"). By using the
						Service, you agree to these Terms.
					</Typography>
				</Paper>

				<Grid container spacing={3} sx={{ mb: 4 }}>
					{[
						{
							title: "Acceptable use",
							desc: "Use Orucore responsibly, lawfully, and without attempting unauthorized access.",
						},
						{
							title: "Account security",
							desc: "You are responsible for your credentials and all activity on your account.",
						},
						{
							title: "Payments",
							desc: "Subscriptions are billed in advance with taxes where applicable.",
						},
						{
							title: "Termination",
							desc: "We may suspend accounts that violate these Terms or pose security risks.",
						},
					].map((item) => (
						<Grid item xs={12} md={6} key={item.title}>
							<Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
								<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
									{item.title}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{item.desc}
								</Typography>
							</Paper>
						</Grid>
					))}
				</Grid>

				<Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
					<Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
						Terms Details
					</Typography>
					<Divider sx={{ mb: 3 }} />

					<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
						1. Eligibility & Access
					</Typography>
					<Typography variant="body2" color="text.secondary">
						You must be legally able to enter a contract to use Orucore. Access
						may be restricted in jurisdictions where the Service is not
						available.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						2. Acceptable Use
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Do not misuse the Service or attempt unauthorized access." />
						</ListItem>
						<ListItem>
							<ListItemText primary="No illegal, harmful, or abusive content may be uploaded." />
						</ListItem>
						<ListItem>
							<ListItemText primary="You must comply with all applicable laws and regulations." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						3. Account Responsibilities
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Maintain confidentiality of your login credentials." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Provide accurate information and keep it up to date." />
						</ListItem>
						<ListItem>
							<ListItemText primary="You are responsible for all activities under your account." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						4. Subscriptions, Payments & Refunds
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Paid plans are billed in advance according to your subscription." />
						</ListItem>
						<ListItem>
							<ListItemText primary="All fees are non-refundable unless required by law or stated otherwise." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Taxes may apply based on your location." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						5. Data Ownership
					</Typography>
					<Typography variant="body2" color="text.secondary">
						You retain ownership of the data you upload. You grant Orucore a
						limited license to process that data solely to provide the Service.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						6. Intellectual Property
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Orucore and its content, trademarks, and software are owned by us or
						our licensors. You may not copy, modify, or distribute without
						permission.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						7. Availability & Changes
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We aim to keep Orucore available and reliable, but we do not promise
						uninterrupted access. We may modify or discontinue features with or
						without notice.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						8. Termination & Suspension
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We may suspend or terminate accounts that violate these Terms or pose
						a security risk. You may cancel your account at any time.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						9. Disclaimers & Limitation of Liability
					</Typography>
					<Typography variant="body2" color="text.secondary">
						To the fullest extent permitted by law, Orucore is provided "as is"
						and we are not liable for indirect, incidental, or consequential
						damages resulting from your use of the Service.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						10. Dispute Resolution & Governing Law
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Any disputes will be resolved through good-faith negotiation. If
						needed, disputes will be handled in the courts of the governing
						jurisdiction. These Terms are governed by the laws applicable to
						Orucore's primary place of business.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						11. Changes to Terms
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We may update these Terms from time to time. Continued use of the
						Service means you accept the updated Terms.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						12. Contact Us
					</Typography>
					<Typography variant="body2" color="text.secondary">
						For questions about these Terms, contact us at
						<strong> support@orucore.com</strong>.
					</Typography>
				</Paper>
			</Container>
		</Box>
	);
};

export default TermsAndConditions;
